import type { ApiErrorBody } from '@minisoc/contracts';
import type { SessionStore } from './session.js';

/** Erreur enrichie du corps normalisé renvoyé par l'API. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** `true` quand l'erreur relève de la session et non de la requête. */
  get isAuthError(): boolean {
    return this.status === 401;
  }
}

export interface HttpClientOptions {
  readonly baseUrl: string;
  readonly session: SessionStore;
  /** Appelé quand la session est définitivement perdue, pour rediriger vers le login. */
  readonly onSessionExpired?: () => void;
}

/**
 * Client HTTP avec rafraîchissement transparent du token.
 *
 * Deux points de conception :
 *
 * - Une seule tentative de rafraîchissement par requête. Sans ce garde-fou, un
 *   refresh token invalide provoquerait une boucle de rafraîchissement infinie.
 * - Rafraîchissement mutualisé. Quand plusieurs requêtes échouent en 401
 *   simultanément (le dashboard en émet trois au montage), elles partagent la
 *   même promesse de rafraîchissement. Sans cela, la première rotation
 *   révoquerait le token que les suivantes s'apprêtent à présenter, et la session
 *   serait perdue alors qu'elle était valide.
 */
export class HttpClient {
  private refreshInFlight: Promise<boolean> | null = null;

  constructor(private readonly options: HttpClientOptions) {}

  get baseUrl(): string {
    return this.options.baseUrl;
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body);
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body);
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  /** Requête sans authentification, pour le login. */
  async anonymous<T>(method: string, path: string, body?: unknown): Promise<T> {
    const response = await fetch(`${this.options.baseUrl}${path}`, {
      method,
      headers: { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    return this.parse<T>(response);
  }

  private async request<T>(method: string, path: string, body?: unknown, retried = false): Promise<T> {
    const session = this.options.session.read();

    const response = await fetch(`${this.options.baseUrl}${path}`, {
      method,
      headers: {
        'content-type': 'application/json',
        ...(session ? { authorization: `Bearer ${session.accessToken}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (response.status === 401 && !retried && session) {
      const refreshed = await this.refresh();
      if (refreshed) return this.request<T>(method, path, body, true);

      this.options.session.clear();
      this.options.onSessionExpired?.();
    }

    return this.parse<T>(response);
  }

  private refresh(): Promise<boolean> {
    this.refreshInFlight ??= this.performRefresh().finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }

  private async performRefresh(): Promise<boolean> {
    const session = this.options.session.read();
    if (!session) return false;

    try {
      const tokens = await this.anonymous<{
        accessToken: string;
        refreshToken: string;
        expiresIn: number;
      }>('POST', '/auth/refresh', { refreshToken: session.refreshToken });

      this.options.session.updateTokens(tokens);
      return true;
    } catch {
      return false;
    }
  }

  private async parse<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const payload = text.length > 0 ? (JSON.parse(text) as unknown) : null;

    if (!response.ok) {
      const error = payload as ApiErrorBody | null;
      throw new ApiError(
        response.status,
        error?.code ?? 'UNKNOWN',
        error?.message ?? `Erreur HTTP ${response.status}`,
        error?.details,
      );
    }

    return payload as T;
  }
}

/** Sérialise un objet de filtres en chaîne de requête, en omettant les vides. */
export function toQueryString(params: Record<string, unknown>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;

    if (Array.isArray(value)) {
      if (value.length > 0) search.set(key, value.join(','));
      continue;
    }

    search.set(key, value instanceof Date ? value.toISOString() : String(value));
  }

  const query = search.toString();
  return query.length > 0 ? `?${query}` : '';
}
